import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { useTranslation } from "react-i18next";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { CustomDatePicker } from "@components/Forms";
import TableWithPagination from "@components/Tables/TableWithPagination";
import CustomSelect from "@components/UI/CustomSelect";
import FileUploader from "@components/UI/FileUploader";
import LicenseSelectionModal from "@components/UI/LicenseSelectionModal";
import MemberSelectionModal from "@components/UI/MemberSelectionModal";
import PreviewSelectionModal from "@components/UI/PreviewSelectionModal";
import { useAddProjectForm } from "@hooks/useAddProjectForm";
import { useTheme } from "@hooks/useTheme";
import { CHARACTER_LENGTH } from "@constants/index";
import useEscapeKey from "@hooks/useEscapeKey";

const AddProjectModal = ({
  isOpen,
  onClose,
  productSeq, // Auto-filled product
  onProjectAdded,
  "data-testid": dataTestId = "add-project-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { colors } = useTheme();
  const { t } = useTranslation();
  const {
    assetClassificationsList,
    canAddProjectMemberPermission,
    creatorList,
    creatorOptions,
    departmentList,
    distributionScopeList,
    formData,
    handleDeleteMember,
    handleInputChange,
    handleLicenseSelect,
    handleMemberSelect,
    handlePreviewSelect,
    handleProjectManagerChange,
    handleSave,
    isFormValid,
    isPreviewEnabled,
    isPreviewModalOpen,
    licensePolicyList,
    loading,
    modalSelectLicenseOpen,
    modalSelectMemberOpen,
    productsList,
    projectManagerList,
    resetFormState,
    setIsPreviewModalOpen,
    setModalSelectLicenseOpen,
    setModalSelectMemberOpen,
  } = useAddProjectForm({
    productSeq,
    isActive: isOpen,
    resetOnActivate: true,
    resetOnCreateCompleted: true,
    onCreateCompleted: onProjectAdded,
  });

  const handleCancel = () => {
    resetFormState();
    onClose();
  };

  // Table config for selected members
  const memberTableColumns = ["userId", "userName", "email"];
  const memberTableColumnConfig = {
    userId: {
      style: { width: "20%", textAlign: "left", padding: "0.5rem" },
      headerRender: () => t("common.user-id", "USER ID"),
    },
    userName: {
      style: { width: "30%", textAlign: "left", padding: "0.5rem" },
      headerRender: () => t("common.user-name", "USER NAME"),
      customRender: (value, row) => (
        <div className="flex items-center space-x-3">
          <span>{row.userName}</span>
        </div>
      ),
    },
    email: {
      style: { width: "35%", textAlign: "left", padding: "0.5rem" },
      headerRender: () => t("common.email", "EMAIL"),
    },
  };

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center backdrop-blur-[1px] overflow-x-auto p-4`} data-testid={`${dataTestId}-overlay`}>
      <div className={`${colors.bgModal} max-h-[90vh] w-[900px] min-w-[900px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`} data-testid={dataTestId}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>{t("project.add-new-project")}</h2>
          </div>
        </div>

        {/* Main Content - Scrollable */}
        <div className="flex flex-1 flex-col px-6 min-h-0 overflow-y-auto" data-testid={`${dataTestId}-content`}>
          <div className={`mx-auto max-w-[55rem]`}>
            {/* Project Information Section */}
            <div className="mb-6">
              <h3 className={`${colors.textPrimary} text-lg font-semibold mb-4`}>
                {t("project.project-information-title")}
              </h3>
              <p className={`mb-4 ${colors.textSubTitle} text-sm font-normal`} data-testid={`${dataTestId}-description`}>
                {t("project.required-fields-note")} <span className={colors.textDanger}>*</span>
              </p>

              <div className="grid grid-cols-3 gap-6">
                {/* Project Name */}
                <div className="col-span-2" data-testid={fieldContainerTestId("project-name")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("project-name")}>
                    {t("project.clarity.project-name")} <span className={colors.textDanger}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t("project.project-name-placeholder")}
                    value={formData.projectName}
                    onChange={(e) => handleInputChange("projectName", e.target.value)}
                    title={formData.projectName}
                    className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                    maxLength={1024}
                    data-testid={fieldInputTestId("project-name")}
                  />
                </div>

                {/* Product Name - Readonly since it's auto-filled */}
                <div className="relative" data-testid={fieldContainerTestId("product")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("product")}>
                    {t("project.product-name")} <span className={colors.textDanger}>*</span>
                  </label>
                  <CustomSelect
                    options={productsList || []}
                    value={productsList?.find((opt) => opt.value === formData.productSeq) || null}
                    onChange={() => { }} // Read-only
                    placeholder={t("common.please-select")}
                    isSearchable={false}
                    isClearable={false}
                    isDisabled={true}
                    className="w-full text-sm font-medium"
                    data-testid={fieldInputTestId("product")}
                  />
                </div>

                {/* Completion Request Date */}
                <div className="relative" data-testid={fieldContainerTestId("completion-request-date")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("completion-request-date")}>
                    {t("project.clarity.completion-request-date")} <span className={colors.textDanger}>*</span>
                  </label>
                  <CustomDatePicker
                    selected={formData.completionRequestDate}
                    onChange={(date) => handleInputChange("completionRequestDate", date)}
                    placeholderText={t("project.select-date-placeholder")}
                    title={formData.completionRequestDate ? formData.completionRequestDate.toLocaleDateString() : ""}
                    className={`h-10 w-full appearance-none rounded-lg border p-3 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                    dateFormat="yyyy-MM-dd"
                    minDate={new Date()}
                    data-testid={fieldInputTestId("completion-request-date")}
                  />
                </div>

                {/* Project Manager */}
                <div className="relative" data-testid={fieldContainerTestId("project-manager")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("project-manager")}>
                    {t("project.project-manager")}
                  </label>
                  <CustomSelect
                    options={projectManagerList || []}
                    value={projectManagerList?.find((opt) => opt.value === formData.projectManagerId) || null}
                    onChange={handleProjectManagerChange}
                    placeholder={t("common.please-select")}
                    isSearchable={true}
                    isClearable={true}
                    displayFormatLabelWithID={true}
                    className="w-full text-sm font-medium"
                    data-testid={fieldInputTestId("project-manager")}
                  />
                </div>

                {/* Asset Classification */}
                <div data-testid={fieldContainerTestId("asset-classification")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("asset-classification")}>
                    {t("project.clarity.asset-classification")}
                  </label>
                  <CustomSelect
                    options={assetClassificationsList || []}
                    value={assetClassificationsList?.find((opt) => opt.value === formData.assetClassification) || null}
                    onChange={(opt) => handleInputChange("assetClassification", opt?.value || null)}
                    placeholder={t("common.please-select")}
                    isSearchable={false}
                    clearable={false}
                    className="w-full text-sm font-medium"
                    data-testid={fieldInputTestId("asset-classification")}
                  />
                </div>

                {/* Distribution Scope */}
                <div data-testid={fieldContainerTestId("distribution-scope")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("distribution-scope")}>
                    {t("project.clarity.distribution")}
                  </label>
                  <CustomSelect
                    options={distributionScopeList || []}
                    value={distributionScopeList?.find((opt) => opt.value === formData.distributionScope) || null}
                    onChange={(opt) => handleInputChange("distributionScope", opt?.value || null)}
                    placeholder={t("common.please-select")}
                    isSearchable={false}
                    clearable={false}
                    className={`w-full text-sm font-medium ${colors.textPrimary}`}
                    data-testid={fieldInputTestId("distribution-scope")}
                  />
                </div>

                {/* License Policy */}
                <div className="col-span-2" data-testid={fieldContainerTestId("license-policy")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("license-policy")}>
                    {t("project.clarity.license-policy")}
                  </label>
                  <CustomSelect
                    options={licensePolicyList || []}
                    value={licensePolicyList?.find((opt) => opt.value === formData.licensePolicy) || null}
                    onChange={(opt) => handleInputChange("licensePolicy", opt?.value || null)}
                    placeholder={t("common.please-select")}
                    isSearchable={false}
                    clearable={false}
                    className="w-full text-sm font-medium"
                    data-testid={fieldInputTestId("license-policy")}
                  />
                </div>

                {/* License */}
                <div className="relative" data-testid={fieldContainerTestId("license")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("license")}>
                    {t("project.clarity.license")}
                  </label>
                  <div>
                    <input
                      type="text"
                      placeholder={t("project.select-license-placeholder")}
                      value={formData.selectedLicense?.licenseName || ""}
                      readOnly
                      onClick={() => setModalSelectLicenseOpen(true)}
                      title={formData.selectedLicense?.licenseName || ""}
                      className={`h-10 w-full cursor-pointer rounded-lg border truncate ps-3 pr-10 py-2 text-sm font-medium focus:outline-none ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                      data-testid={fieldInputTestId("license")}
                    />
                    <MagnifyingGlassIcon
                      className={`pointer-events-none absolute top-10 right-4 h-4 w-4 ${colors.textActive}`}
                    />
                  </div>
                </div>

                {/* Preview List */}
                {isPreviewEnabled && (
                  <div className="col-span-2 relative" data-testid={fieldContainerTestId("preview")}>
                    <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("preview")}>
                      {t("verification-comparison.preview-list")}
                    </label>
                    <div>
                      <input
                        type="text"
                        placeholder={t("project.select-preview-title")}
                        value={formData.selectedProjectPreview?.projectPreviewName || ""}
                        readOnly
                        onClick={() => setIsPreviewModalOpen(true)}
                        className={`h-10 w-full cursor-pointer rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                        data-testid={fieldInputTestId("preview")}
                      />
                      <MagnifyingGlassIcon
                        className={`pointer-events-none absolute top-10 right-4 h-4 w-4 ${colors.textActive}`}
                      />
                    </div>
                  </div>
                )}

                {/* Remark */}
                <div className="col-span-3" data-testid={fieldContainerTestId("remark")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("remark")}>
                    {t("common.remark")}
                  </label>
                  <textarea
                    rows={4}
                    placeholder={t("project.remark-placeholder")}
                    value={formData.remark}
                    onChange={(e) => handleInputChange("remark", e.target.value)}
                    title={formData.remark}
                    maxLength={CHARACTER_LENGTH.REMARK}
                    className={`w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                    data-testid={fieldInputTestId("remark")}
                  />
                  <p className={`mt-1 text-xs ${colors.textCharacterCount}`} data-testid={`${dataTestId}-field-remark-char-count`}>
                    {(formData.remark || "").length}/{CHARACTER_LENGTH.REMARK}
                  </p>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className={`mb-6`} data-testid={fieldContainerTestId("documents")}>
              <h3 className={`${colors.textPrimary} mb-2 font-bold`} data-testid={fieldLabelTestId("documents")}>{t("project.documents")}</h3>
              <FileUploader
                onFilesChange={(files) => handleInputChange("selectedFiles", files)}
                selectedFiles={formData.selectedFiles}
                data-testid={fieldInputTestId("documents")}
              />
            </div>

            {canAddProjectMemberPermission && (
            <div className={colors.borderPrimary}>
              <h3 className={`${colors.textPrimary} mb-2 font-bold uppercase`}>{t("project.members")}</h3>

              {/* Display selected members */}
              {formData.selectedMembers.length > 0 && (
                <div className="mb-4">
                  <TableWithPagination
                    columns={memberTableColumns}
                    columnConfig={memberTableColumnConfig}
                    data={formData.selectedMembers}
                    currentPage={1}
                    setCurrentPage={() => {}}
                    loading={false}
                    rowKey="userId"
                    totalPages={1}
                    totalData={formData.selectedMembers.length}
                    maxRowsPerPage={formData.selectedMembers.length}
                    hasCheckbox={false}
                    hasDeleteColumn={true}
                    onDeleteRow={handleDeleteMember}
                    showPagination={false}
                    onItemsPerPageChange={() => {}}
                  />
                </div>
              )}

              {/* Add member button */}
              <div
                className={`${colors.bgDropFiles} border-1 border-dashed ${colors.borderActiveDashed} rounded-lg p-1 text-center`}
              >
                <div className="flex justify-center not-first-of-type:items-center">
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => setModalSelectMemberOpen(true)}
                      className={`px-4 py-2 text-sm font-semibold ${colors.textActive} rounded-lg transition-all duration-1000 ease-in-out`}
                      data-testid={`${dataTestId}-add-member-button`}
                    >
                      <span className="text-sm font-semibold">
                        {formData.selectedMembers.length > 0 ? t("project.add-more-members") : t("project.add-member")}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Footer - Sticky */}
        <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-3">
              <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
              <PrimaryButton data-testid={`${dataTestId}-confirm-button`} onClick={handleSave} disabled={!isFormValid() || loading}>
                {t("common.save-button")}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Selection Modal */}
      <PreviewSelectionModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        onSelect={handlePreviewSelect}
        selectedPreview={formData.selectedProjectPreview}
        creatorList={creatorList}
      />

      {/* License Selection Modal */}
      <LicenseSelectionModal
        isOpen={modalSelectLicenseOpen}
        onClose={() => setModalSelectLicenseOpen(false)}
        onSelect={handleLicenseSelect}
        selectedLicense={formData.selectedLicense}
      />

      {canAddProjectMemberPermission && (
        <MemberSelectionModal
          isOpen={modalSelectMemberOpen}
          onClose={() => setModalSelectMemberOpen(false)}
          onSelect={handleMemberSelect}
          selectedMember={formData.selectedMembers}
          creatorList={creatorOptions}
          departmentList={departmentList}
          projectManagerId={formData.projectManagerId || null}
        />
      )}
    </div>
  );
};

export default AddProjectModal;